import { createPatchData } from 'features/fde/helpers/rolodex/createPatchData'
import {
  FieldValue,
  FieldValues,
  getComponentSubtree,
  stripComponents,
} from 'features/libra/ontology/ontologyFieldTypes'
import { useComponentScope } from 'features/libra/workspace/ComponentScopeContext'
import { useCallback, useMemo } from 'react'
import { RolodexEntryTagsType } from 'types'

import { applyOriginOption, FieldOriginOption } from './fieldOrigin'
import {
  buildFieldValuesPatch,
  getScopedFieldValues,
  toPersistedShape,
} from './fieldValuesPatch'
import { useTicketAccess } from './TicketAccessContext'
import { useTicketWriter } from './TicketWriterContext'

/** Selects the ticket-root slice or one component's field-values subtree. */
export type FieldAccessScopePath =
  | { kind: 'ticket' }
  | { kind: 'component'; componentId: string }

export interface ScopedFieldAccess {
  fieldValues: FieldValues
  committedFieldValues: FieldValues
  getCommittedFieldValues: () => FieldValues
  enqueueFieldValues: (
    prepare: (
      latest: FieldValues,
      ticketId: string
    ) => Partial<FieldValues> | null
  ) => Promise<FieldValues | null>
  setFieldValue: (
    fieldId: string,
    fieldValue: FieldValue,
    option?: FieldOriginOption
  ) => Promise<void>
  setFieldValues: (
    values: Partial<FieldValues>,
    option?: FieldOriginOption
  ) => Promise<boolean>
  readOnly: boolean
  isLoading: boolean
  ticketTags: RolodexEntryTagsType
}

const emptyFieldValues: FieldValues = Object.freeze({})

/** Reads and writes only the active ticket or component field-values slice. */
export const useScopedFieldAccess = (
  scope: FieldAccessScopePath
): ScopedFieldAccess => {
  const access = useTicketAccess()
  const { enqueue, committedTicket, getCommittedTicket } = useTicketWriter()
  const {
    readOnly,
    isLoading,
    ticketTags,
    fieldValues: rawTicketFieldValues,
  } = access
  // Callers recreate the scope object during render; memoize by its id.
  const scopeKind = scope.kind
  const scopedComponentId =
    scope.kind === 'component' ? scope.componentId : undefined
  const stableScope = useMemo<FieldAccessScopePath>(
    () =>
      scopedComponentId === undefined
        ? { kind: 'ticket' }
        : { kind: 'component', componentId: scopedComponentId },
    [scopedComponentId]
  )
  const committedFieldValues = useMemo(
    () => getScopedFieldValues(committedTicket.fieldValues, stableScope),
    [committedTicket.fieldValues, stableScope]
  )
  const getCommittedFieldValues = useCallback(
    () => getScopedFieldValues(getCommittedTicket().fieldValues, stableScope),
    [getCommittedTicket, stableScope]
  )

  const enqueueFieldValues = useCallback(
    (
      prepare: (
        latest: FieldValues,
        ticketId: string
      ) => Partial<FieldValues> | null
    ) => {
      if (readOnly) return Promise.resolve(null)
      return enqueue(
        (latest) => {
          const latestFields = getScopedFieldValues(
            latest.fieldValues,
            stableScope
          )
          const values = prepare(latestFields, latest.id)
          if (!values) return null

          const fieldValues = buildFieldValuesPatch(
            latest.fieldValues,
            stableScope,
            values
          )
          const appliedFields = getScopedFieldValues(fieldValues, stableScope)
          if (
            createPatchData(
              'id',
              toPersistedShape(latestFields),
              toPersistedShape(appliedFields)
            ) === undefined
          ) {
            return null
          }
          return { fieldValues }
        },
        { applyInteractionSideEffects: true }
      ).then((applied) =>
        applied?.fieldValues
          ? getScopedFieldValues(applied.fieldValues, stableScope)
          : null
      )
    },
    [enqueue, readOnly, stableScope]
  )

  const setFieldValues = useCallback(
    async (
      values: Partial<FieldValues>,
      option?: FieldOriginOption
    ): Promise<boolean> => {
      if (readOnly) return false
      if (scopeKind === 'ticket' || scopedComponentId === undefined) {
        return access.setFieldValues(values as FieldValues, option)
      }
      const applied = await enqueue(
        (latest) => ({
          fieldValues: buildFieldValuesPatch(
            latest.fieldValues,
            { kind: 'component', componentId: scopedComponentId },
            applyOriginOption(values as FieldValues, option)
          ),
        }),
        { applyInteractionSideEffects: true }
      )
      return applied !== null
    },
    [readOnly, scopeKind, scopedComponentId, access, enqueue]
  )

  const setFieldValue = useCallback(
    async (
      fieldId: string,
      fieldValue: FieldValue,
      option?: FieldOriginOption
    ): Promise<void> => {
      await setFieldValues({ [fieldId]: fieldValue }, option)
    },
    [setFieldValues]
  )

  if (scope.kind === 'ticket') {
    return {
      fieldValues: stripComponents(rawTicketFieldValues),
      committedFieldValues,
      getCommittedFieldValues,
      enqueueFieldValues,
      setFieldValue,
      setFieldValues,
      readOnly,
      isLoading,
      ticketTags,
    }
  }

  const componentFieldValues =
    getComponentSubtree(rawTicketFieldValues)?.[scope.componentId] ??
    emptyFieldValues

  return {
    fieldValues: componentFieldValues,
    committedFieldValues,
    getCommittedFieldValues,
    enqueueFieldValues,
    setFieldValue,
    setFieldValues,
    readOnly,
    isLoading,
    ticketTags,
  }
}

/**
 * Field access for the ACTIVE scope: component-scoped when a
 * `ComponentScopeContext` is mounted (inside `UnitComponentTabs`), else
 * ticket scope. One call replaces the scope-derivation ternary consumers
 * used to re-implement at every call site.
 */
export function useActiveScopedFieldAccess(): ScopedFieldAccess {
  const componentScope = useComponentScope()
  return useScopedFieldAccess(
    componentScope
      ? { kind: 'component', componentId: componentScope.componentId }
      : { kind: 'ticket' }
  )
}
